import { DndContext } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { REQUIRABLE_DEFAULT_FIELDS } from 'constants/FormFieldType';
import useLaunchDarkly from 'hooks/useLaunchDarkly';
import PropTypes from 'prop-types';
import React from 'react';
import FormConstructorField from './FormConstructorField';
import NewFieldDropdown from './NewFieldDropdown';
import './style.scss';

const FormConstructorV2 = ({ fields = [], description, onChange, customAttributesKeeper }) => {
  const flags = useLaunchDarkly();
  const _isDraggable = field =>
    // default fields that have a mutable 'required' state
    REQUIRABLE_DEFAULT_FIELDS.includes(field.mappers?.[0]?.attribute_name) ||
    // user-added fields
    field.removable !== false ||
    // everything on auth pickup, policy/waiver
    !customAttributesKeeper;
  const mapIsDraggable = fields.reduce((acc, field) => ({ ...acc, [field.id]: _isDraggable(field) }), {});
  const isDragEnabled =
    Object.values(mapIsDraggable).filter(Boolean).length > 1 && flags['reorder-registration-optional-fields'];
  const isDraggable = field => isDragEnabled && mapIsDraggable[field.id];

  const addField = field => {
    const nextFields = [...fields, field];
    handleChange(nextFields);
  };

  const updateField = (fieldIndex, fieldData) => {
    const nextFields = [...fields.slice(0, fieldIndex), fieldData, ...fields.slice(fieldIndex + 1)];

    handleChange(nextFields);
  };

  const deleteField = fieldIndex => {
    const nextFields = [...fields.slice(0, fieldIndex), ...fields.slice(fieldIndex + 1)];

    handleChange(nextFields);
  };

  const handleChange = nextFields => {
    if (!onChange) {
      return;
    }

    onChange(nextFields);
  };

  const openAddFieldModal = type => {
    const formPayload = {
      actionType: 'create',
      fieldType: type,
      totalFields: fields.length,
      customAttributesEnabled: !!customAttributesKeeper,
      customAttributesKeeper: customAttributesKeeper,
      customAttributeIdsAlreadyBound: fields.map(field => field.options?.customAttributeId).filter(Boolean)
    };

    Actions.showModal('CreateFormFieldV2', formPayload).then(field => {
      if (field) {
        addField(field);
      }
    });
  };

  const openEditFieldModal = fieldId => {
    if (!fieldId) {
      return;
    }

    const fieldIndex = fields.findIndex(f => f.id === fieldId);
    if (fieldIndex === -1) {
      return;
    }

    const formPayload = {
      actionType: 'edit',
      fieldObj: fields[fieldIndex],
      totalFields: fields.length,
      customAttributesEnabled: !!customAttributesKeeper,
      customAttributesKeeper: customAttributesKeeper,
      customAttributeIdsAlreadyBound: fields
        .filter(field => field.id !== fieldId)
        .map(field => field.options?.customAttributeId)
        .filter(Boolean)
    };

    Actions.showModal('CreateFormFieldV2', formPayload).then(fieldData => {
      if (!fieldData) {
        return;
      }
      updateField(fieldIndex, fieldData);
    });
  };

  const openDeleteFieldModal = fieldId => {
    if (!fieldId) {
      return;
    }

    const fieldIndex = fields.findIndex(f => f.id === fieldId);
    if (fieldIndex === -1) {
      return;
    }

    const formPayload = {
      title: 'Delete Field',
      description: 'Are you sure you want to delete this field?'
    };

    Actions.showModal('Confirmation', formPayload).then(res => {
      if (!res) {
        return;
      }

      deleteField(fieldIndex);
    });
  };

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = fields.findIndex(f => f.id === active.id);
      const newIndex = mapIsDraggable[over.id]
        ? fields.findIndex(f => f.id === over.id)
        : // if we're dropping onto a non-draggable field, use the first field that is draggable
          fields.findIndex(f => mapIsDraggable[f.id]);
      const nextFields = arrayMove(fields, oldIndex, newIndex);

      handleChange(nextFields);
    }
  }

  const renderFields = () => {
    return fields
      .filter(f => f.type !== 'button')
      .map((field, i) => (
        <FormConstructorField
          key={field.id}
          id={field.id}
          type={field.type}
          removable={field.removable}
          options={field.options}
          onEdit={openEditFieldModal}
          onDelete={openDeleteFieldModal}
          isDraggable={isDraggable(field)}
        />
      ));
  };

  return (
    <div className="form-constructor">
      <DndContext onDragEnd={handleDragEnd}>
        <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
          <div className="form-constructor__fields">{renderFields()}</div>
        </SortableContext>
      </DndContext>

      <NewFieldDropdown onSelect={openAddFieldModal} />

      {description && <div className="form-constructor__description">{description}</div>}
    </div>
  );
};

FormConstructorV2.propTypes = {
  fields: PropTypes.array,
  description: PropTypes.string,
  onChange: PropTypes.func
};

export default FormConstructorV2;
